/*
As the affiliate of a humanitarian organization seeking to request access
to Love Big, I want a page that summarizes Love Big's mission and goals,
so that I can determine if I would like to sign up.
*/

//Packages
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
//Material UI Components
import { Grid, Stack, Typography, Button } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { makeStyles } from "@mui/styles";
//Animation
import { motion } from "framer-motion";
//Assets
import bottomBkg from "../../assets/images/aboutChangeBkg.png";
import worldMapDot from "../../assets/images/worldmapdot.png";
import africa from "../../assets/images/africa.png";
//Styles
import "./styles.css";

//Material UI Styles
const useStyles = makeStyles((theme) => ({
  topContainer: {
    paddingTop: "30px",
  },
  listItem: {
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "nowrap",
    lineHeight: "20px",
  },
  topList: {
    "& hr": {
      border: "0.05px solid #a3a3a3a8",
    },
  },
  middleContainer: {
    position: "relative",
    backgroundColor: "rgba(51, 156, 213, 0.85)",
    backgroundImage: `url(${worldMapDot})`,
    backgroundPosition: "53% 30%",
    backgroundSize: "1200px",
    backgroundRepeat: "no-repeat",
    color: "#ffffff",
    overflowX: "hidden",
  },
  africa: {
    position: "absolute",
    width: "375px",
    top: "25%",
    zIndex: 0,
  },
  middleList: {},

  exploreProjects: {
    textAlign: "center",
    height: "77px",
    backgroundColor: "#052442",
    color: "#ffffff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  takeActionDiv: {
    backgroundImage: `url(${bottomBkg})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    color: "#ffffff",
    borderRadius: "35px",
    position: "relative",
  },
  takeActionText: {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    alignItems: "center",
    paddingLeft: "5%",
  },
  partnerButton: {
    backgroundColor: "#104d82",
    width: "fit-content",
    fontSize: "16px",
    padding: "10px 15px 10px 15px",
    borderRadius: "50px",
  },
  link: {
    textDecoration: "none",
    margin: "auto",
  },
  faithWrapper: {
    backgroundColor: "rgba(51, 156, 213, 0.85)",
  },
}));

export default function About() {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(null);
  const handleChangeOnMouseHover = (panel) => {
    const isMobile =
      navigator.userAgentData.mobile ||
      (window.innerWidth <= 800 && window.innerHeight <= 900);
    if (isMobile) {
      return;
    }
    if (expanded === panel) {
      setExpanded(null);
    } else {
      setExpanded(panel);
    }
  };
  const handleChangeOnTouchStart = (panel) => {
    if (expanded === panel) {
      setExpanded(null);
    } else {
      setExpanded(panel);
    }
  };
  const accordions = [
    {
      title: "Research is Crowd-Sourced Locally",
      desc: "Through indigenous relationships, NGO's, government, clergy, and a great tech platform",
    },
    {
      title: "Information is Deeply Analyzed",
      desc: "To ensure high accuracy and an impactful response",
    },
    {
      title: "Thoughtful Pairings are Established",
      desc: "Between organizations, projects, and indigenous partners by category, location, and priority",
    },
    {
      title: "Gates are Opened and Game Changers Mobilized",
      desc: "With high integrity, high impact organizations and indigenous project partners to resolve these challenges together",
    },
    {
      title: "Economic Viability is Enabled",
      desc: "Beyond sustainability, leaving long-term vitality in the wake, in partnership with local economic mentors",
    },
    {
      title: "Pastoring is Offered",
      desc: "Because spiritually well is the only lasting answer",
    },
  ];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="about">
        <Grid
          container
          className={styles.topContainer}
          justifyContent={{ xs: "center", sm: "space-evenly" }}
        >
          <Grid item xs={9} md={4}>
            <Typography
              variant="h4"
              fontWeight="100"
              pt={{ xs: 0, md: 4 }}
              mb={4}
            >
              A High Integrity Approach
            </Typography>
            <Typography variant="subtitle1" fontWeight="100">
              Love Big and our indigenous partners have done the groundwork to
              provide data on needs relating to water, food, medicine, and other
              projects. Local and international organizations who know the
              geography adopt these projects. We provide them with local
              contacts and the coordinates for vetted projects, and they go to
              work. Our partner organizations and indigenous partners both
              report progress on our easy-to-use web app. In the end, lives are
              changed and communities are transformed.
            </Typography>
          </Grid>
          <Grid item xs={9} md={6} mt={{ xs: 4, md: 2 }} mb={{ xs: 4, md: 7 }}>
            <Stack
              spacing={{ xs: 2, md: 4 }}
              fontSize={{ xs: "14px", md: "20px" }}
              className={styles.topList}
            >
              {/* The client will need to provide the content for the "read more"
                  hover-over buttons, as well as the design for what should be triggered
                  when it is hovered over.
              */}
              <div
                onMouseLeave={() => {
                  handleChangeOnMouseHover("");
                }}
                className="accordion-wrapper"
              >
                {accordions.map((accordion, index) => (
                  <div
                    key={index}
                    onMouseEnter={() => handleChangeOnMouseHover(index)}
                    onTouchStart={() => handleChangeOnTouchStart(index)}
                  >
                    <Accordion
                      square
                      expanded={expanded === index}
                      disableGutters
                    >
                      <AccordionSummary
                        expandIcon={<MoreVertIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                      >
                        <Typography variant="h5">{accordion.title}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography>{accordion.desc}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                ))}
              </div>
            </Stack>
          </Grid>
        </Grid>

        <Grid
          container
          className={styles.middleContainer}
          direction="column"
          p={{ xs: 5, sm: 6 }}
        >
          <Grid container justifyContent="center">
            <img src={africa} className={styles.africa} alt="Africa"></img>
          </Grid>
          <Grid item zIndex={2} pt={8}>
            <Stack spacing={2}>
              <Typography variant="h4" fontWeight="100" align="center">
                Creating A Lasting Impact
              </Typography>
            </Stack>
          </Grid>
          <Grid item mt={8} mb={8} zIndex={2} alignItems="center">
            <Stack spacing={5} className={styles.middleList}>
              <Stack spacing={1}>
                <Typography
                  variant="h6"
                  fontWeight="400"
                  lineHeight="25px"
                  align="center"
                >
                  {" "}
                  Enabling Functional, Economically
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="400"
                  lineHeight="25px"
                  align="center"
                >
                  Viable, and Missional Communities
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <Typography
                  variant="h6"
                  fontWeight="400"
                  lineHeight="25px"
                  align="center"
                >
                  Setting the Stage For Lasting Peace
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="400"
                  lineHeight="25px"
                  align="center"
                >
                  And Functional Generations To Come
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid container item xs={12} justifyContent="center" mb={6} pb={6}>
            <Stack direction="column">
              <Typography
                variant="h4"
                fontWeight="100"
                align="center"
                mb={4}
                zIndex={1}
              >
                Explore Projects
              </Typography>
              <Link to="/explore" className={styles.link}>
                <Button color="primary" variant="contained" size="small">
                  See our Projects
                </Button>
              </Link>
            </Stack>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          justifyContent={{ xs: "center", sm: "space-around" }}
          alignItems={{ sm: "flex-start" }}
          mt={4}
          mb={4}
        >
          <Grid container item justifyContent="center" xs={11} sm={6}>
            <Grid container item justifyContent="center">
              <Stack
                spacing={2}
                width="90%"
                height="300px"
                pl={12}
                justifyContent="flex-start"
                alignItems="center"
              >
                <Typography
                  variant="h6"
                  fontSize={{ sm: "34px" }}
                  fontWeight="400"
                  lineHeight={{ xs: "22px", sm: "34px" }}
                  mt={12}
                >
                  Take Action
                </Typography>
                <Stack width="100%" justifyContent="center">
                  <Link to="/join" className={styles.link}>
                    {" "}
                    <Button color="light" variant="contained">
                      Become a Partner
                    </Button>
                  </Link>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <Grid container item justifyContent="center" xs={11} sm={6}>
            <Grid container item justifyContent="center">
              <div className="matthew-25">
                <h1>
                  Matthew 25{" "}
                  <i className="fas fa-arrow-right matthew-arrow-mobile"></i>
                </h1>
                <h2>
                  35 For I was hungry and you gave me something to eat, I was
                  thirsty and you gave me something to drink, I was a stranger
                  and you invited me in, 36 I needed clothes and you clothed me,
                  I was sick and you looked after me, I was in prison and you
                  came to visit me.’ 40 “The King will reply, ‘Truly I tell you,
                  whatever you did for one of the least of these brothers and
                  sisters of mine, you did for me.’
                </h2>
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Stack
          spacing={{ xs: 4, md: 6 }}
          pt={6}
          pb={6}
          className={styles.faithWrapper}
        >
          <Grid container justifyContent="center">
            <Grid container item xs={9} md={5}>
              <Stack textAlign="center" spacing={1}>
                <Typography variant="h4" color="white">
                  Faith
                </Typography>
                <Typography variant="body2" color="white">
                  We are faith based and believe we were created to reflect
                  God’s love to all. The Bible and historical writers record
                  that Jesus associated with, served, healed and taught the
                  oppressed, the sick, the hungry, the economically distressed,
                  and the afflicted. Love Big was built to reflect this. Jesus
                  made clear the priority of loving God and our neighbors - The
                  “greatest” commandments. We attempt to live this out wherever
                  we go and with everyone we encounter.
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </div>
    </motion.div>
  );
}
